import React, { useEffect, useRef, useState } from 'react';
import { Layout, Spin } from 'antd';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import PosAppHeader from '../AppHeader/PosAppHeader';
import { deviceAuthService, deviceRegisterService, deviceCashierService } from '../../services/posApi';
import { PageTitleProvider } from '../../context/PageTitleContext';
import { useProductsSync } from '../../hooks/useProductsSync';

const { Content } = Layout;

// Функция для декодирования JWT токена
const decodeJWT = (token) => {
    try {
        if (!token) return null;

        const parts = token.split('.');
        if (parts.length !== 3) return null;

        const payload = parts[1];
        // Добавляем padding если необходимо
        const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
        const decoded = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));

        return JSON.parse(decoded);
    } catch (error) {
        console.error('Ошибка при декодировании JWT:', error);
        return null;
    }
};

const isCashierAuthorized = () => {
    const accessToken = localStorage.getItem('device_access_token');
    const cashierInfo = deviceCashierService.getSavedCashierInfo();

    // Проверяем наличие cashier_id в токене
    if (accessToken) {
        const decodedToken = decodeJWT(accessToken);
        if (decodedToken && decodedToken.cashier_id) {
            return true;
        }
    }

    // Проверяем авторизацию кассира в localStorage
    if (cashierInfo && cashierInfo.data && cashierInfo.data.cashier_authorized === true) {
        return true;
    }

    return false;
};

// Функция для сравнения объектов (упрощенная версия)
const isDataDifferent = (newData, oldData) => {
    if (!oldData) return true;
    if (!newData) return false;

    return JSON.stringify(newData) !== JSON.stringify(oldData);
};

// Внутренний компонент, который использует контекст
const PosMainLayoutInner = () => {
    const hasInitialized = useRef(false);
    const location = useLocation();
    const [isInitializing, setIsInitializing] = useState(false);

    // Хук для синхронизации товаров
    const { syncProducts, isSyncing } = useProductsSync();

    useEffect(() => {
        // Проверяем, что это первая загрузка страницы, а не переход между роутами
        // и что пользователь авторизован
        if (hasInitialized.current || !deviceAuthService.isAuthenticated()) {
            return;
        }

        const initializeData = async () => {
            try {
                setIsInitializing(true);
                console.log('Инициализация данных при загрузке страницы...');

                // 1. Получаем и проверяем информацию об устройстве
                try {
                    const registerInfo = await deviceRegisterService.getRegisterInfo();
                    const savedRegisterInfo = deviceRegisterService.getSavedRegisterInfo();

                    if (isDataDifferent(registerInfo, savedRegisterInfo)) {
                        console.log('Обновление информации об устройстве в localStorage');
                        localStorage.setItem('device_register_info', JSON.stringify(registerInfo));
                    }
                } catch (error) {
                    console.error('Ошибка при получении информации об устройстве:', error);
                }

                // 2. Проверяем наличие cashier_id в access токене
                const accessToken = localStorage.getItem('device_access_token');
                if (accessToken) {
                    const decodedToken = decodeJWT(accessToken);

                    if (decodedToken && decodedToken.cashier_id) {
                        console.log('Найден cashier_id в токене, получаем информацию о кассире...');

                        try {
                            const cashierStatus = await deviceCashierService.getCashierStatus();
                            const savedCashierInfo = deviceCashierService.getSavedCashierInfo();

                            // Проверяем, новее ли полученные данные
                            let shouldUpdate = false;

                            if (!savedCashierInfo) {
                                shouldUpdate = true;
                            } else if (cashierStatus && savedCashierInfo) {
                                // Сравниваем по времени обновления, если есть такое поле
                                if (cashierStatus.updated_at && savedCashierInfo.updated_at) {
                                    shouldUpdate = new Date(cashierStatus.updated_at) > new Date(savedCashierInfo.updated_at);
                                } else {
                                    // Если нет полей времени, сравниваем весь объект
                                    shouldUpdate = isDataDifferent(cashierStatus, savedCashierInfo);
                                }
                            }

                            if (shouldUpdate) {
                                console.log('Обновление информации о кассире в localStorage');
                                localStorage.setItem('device_cashier_info', JSON.stringify(cashierStatus));
                            }
                        } catch (error) {
                            console.error('Ошибка при получении информации о кассире:', error);
                        }
                    }
                }

                // Запускаем синхронизацию товаров только если устройство авторизовано
                console.log('Запуск синхронизации товаров...');
                try {
                    await syncProducts();
                    console.log('Синхронизация товаров завершена');
                } catch (error) {
                    console.error('Ошибка синхронизации товаров при инициализации:', error);
                    // Не блокируем загрузку приложения из-за ошибки синхронизации товаров
                }

                hasInitialized.current = true;
                console.log('Инициализация данных завершена');

            } catch (error) {
                console.error('Ошибка при инициализации данных:', error);
                hasInitialized.current = true;
            } finally {
                setIsInitializing(false);
            }
        };

        // Небольшая задержка для того, чтобы компонент успел отрендериться
        const timeoutId = setTimeout(initializeData, 100);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [location.pathname, syncProducts]); // Добавляем syncProducts в зависимости

    // Показываем спиннер во время инициализации или синхронизации товаров
    if (isInitializing || isSyncing) {
        return (
            <Layout style={{
                minHeight: '100vh',
                backgroundColor: 'var(--ant-color-bg-base)'
            }}>
                <PosAppHeader />
                <Content
                    style={{
                        marginTop: 44,
                        padding: 0,
                        minHeight: 'calc(100vh - 44px)',
                        backgroundColor: 'var(--ant-color-bg-base)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                >
                    <Spin size="large" />
                    <div style={{
                        marginTop: 16,
                        fontSize: '14px',
                        color: 'var(--ant-color-text-secondary)'
                    }}>
                        {isInitializing && 'Инициализация приложения...'}
                        {isSyncing && 'Синхронизация товаров...'}
                    </div>
                </Content>
            </Layout>
        );
    }

    return (
        <Layout style={{
            minHeight: '100vh',
            backgroundColor: 'var(--ant-color-bg-base)'
        }}>
            <PosAppHeader />

            <Content
                style={{
                    marginTop: 44,
                    padding: 0,
                    minHeight: 'calc(100vh - 44px)',
                    backgroundColor: 'var(--ant-color-bg-base)'
                }}
            >
                <Outlet />
            </Content>
        </Layout>
    );
};

// Основной компонент с проверкой аутентификации и провайдером контекста
const PosMainLayout = () => {
    const location = useLocation();

    // Страницы, которые не требуют авторизации
    const publicPaths = ['/device/login'];
    const cashierAuthPaths = ['/device/cashier-login'];

    // Проверяем, является ли текущий путь публичным
    const isPublicPath = publicPaths.includes(location.pathname);
    const isCashierAuthPath = cashierAuthPaths.includes(location.pathname);

    // Если это не публичная страница и пользователь не авторизован - редиректим
    if (!isPublicPath && !deviceAuthService.isAuthenticated()) {
        return <Navigate to="/device/login" />;
    }

    // Если девайс авторизован но кассир нет то редирект на авторизацию кассира
    if (deviceAuthService.isAuthenticated() && !isCashierAuthPath && !isCashierAuthorized()) {
        return <Navigate to="/device/cashier-login" />;
    }

    if (location.pathname === '/device' && isCashierAuthorized()) {
        return <Navigate to="/device/sale" replace />;
    }

    return (
        <PageTitleProvider>
            <PosMainLayoutInner />
        </PageTitleProvider>
    );
};

export default PosMainLayout;